import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Input,
  Button,
  Card,
  message,
  Spin,
  DatePicker,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { getDataForPrivewMap } from "../../api/maps/previewMapRequest";
import { checkParkingPermission } from "../../api/subscriptions/subscriptionsRequest";
import { getTypeLines } from "../../api/typeLine/typeLineRequest";
import dayjs from "dayjs";

const { Option } = Select;

const ParkingCheckSimulation = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState({
    section: [],
    street: [],
    zone: [],
    line: [],
  });
  const [typeLines, setTypeLines] = useState([]);
  const [locationType, setTypeLocation] = useState("zone");
  const [location, setLocation] = useState([]);

  // Fetch location data on component mount
  useEffect(() => {
    fetchLocations();
    fetchLineTypes();
  }, []);

  // Update location when locationType or locations change
  useEffect(() => {
    if (locations[locationType]) {
      setLocation(locations[locationType]);
    }
  }, [locationType, locations]);

  useEffect(() => {
    // Set default date to today using dayjs instead of moment
    form.setFieldsValue({
      check_date: dayjs(),
    });
  }, [form]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const data = await getDataForPrivewMap();

      setLocations({
        zone: data.zones,
        section: data.sections,
        street: data.streets,
        line: data.lines,
      });
      // Set initial location data
      if (data.zones) {
        setLocation(data.zones);
      }
    } catch (error) {
      message.error("Échec de la récupération des données de localisation");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLineTypes = async () => {
    setLoading(true);
    try {
      const data = await getTypeLines();
      setTypeLines(data);
    } catch (error) {
      message.error("Échec de la récupération des types de lignes");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      // Format the date for the API request using dayjs
      if (values.check_date) {
        values.check_date = values.check_date.format("YYYY-MM-DD");
      }

      setLoading(true);
      const response = await checkParkingPermission(values);

      if (response.success) {
        message.success(response.message);
      } else {
        message.info(response.message);
      }
    } catch (error) {
      console.error("Erreur lors de la vérification du stationnement:", error);
      message.error("Échec de la vérification du stationnement");
    } finally {
      setLoading(false);
    }
  };

  const getZoneNameById = (zoneId) => {
    const zone = locations.zone.find((z) => z.id === zoneId);
    return zone ? zone.name : `ID: ${zoneId}`;
  };

  const getSectionNameById = (sectionId) => {
    const section = locations.section.find((sec) => sec.id === sectionId);
    return section ? section.name : `ID: ${sectionId}`;
  };

  const getStreetNameById = (streetId) => {
    const street = locations.street.find((str) => str.id === streetId);
    return street ? street.name : `ID: ${streetId}`;
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          Simulation de Vérification de Stationnement
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Vérifiez si un véhicule peut se garer à un emplacement spécifique.
        </p>
      </div>

      <Card>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="plate_number"
            label="Numéro de plaque"
            rules={[
              {
                required: true,
                message: "Veuillez entrer le numéro de plaque",
              },
            ]}
          >
            <Input placeholder="Entrez le numéro de plaque" />
          </Form.Item>

          <Form.Item
            name="check_date"
            label="Date de vérification"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner une date",
              },
            ]}
          >
            <DatePicker
              placeholder="Sélectionner une date"
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item
            name="location_type"
            label="Type d'emplacement"
            initialValue="zone"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner un type d'emplacement",
              },
            ]}
          >
            <Select
              placeholder="Sélectionner un type d'emplacement"
              onChange={setTypeLocation}
            >
              <Option value="zone">Contrat</Option>
              <Option value="section">zone</Option>
              <Option value="street">Rue</Option>
              <Option value="line">Ligne</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="location_id"
            label="ID de l'emplacement"
            rules={[
              {
                required: true,
                message: "Veuillez entrer l'ID de l'emplacement",
              },
            ]}
          >
            <Select placeholder="Sélectionner un emplacement">
              {location.map((loc) => (
                <Option key={loc.id} value={loc.id}>
                  {locationType === "section" &&
                    getZoneNameById(loc.zone_id) + " > "}
                  {locationType === "street" &&
                    getZoneNameById(
                      locations.section.find((i) => i.id === loc.section_id)
                        ?.zone_id
                    ) +
                      " > " +
                      getSectionNameById(loc.section_id) +
                      " > "}
                  {locationType === "line" &&
                    getStreetNameById(loc.street_id) + " > "}
                  {loc.name || `ID: ${loc.id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="type_line_id"
            label="Type de ligne"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner un type de ligne",
              },
            ]}
          >
            <Select placeholder="Sélectionner un type de ligne">
              {typeLines.map((typeLine) => (
                <Option key={typeLine.id} value={typeLine.id}>
                  {typeLine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Vérifier
            </Button>
          </Form.Item>
        </Form>

        {loading && (
          <div className="flex justify-center p-6">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
          </div>
        )}
      </Card>
    </div>
  );
};

export default ParkingCheckSimulation;
